import { Flag, Inbox, MessageSquare, TriangleAlert } from 'lucide-react';
import {
  Avatar,
  DataTable,
  RangePicker,
  FilterBar,
  FilterDivider,
  MetricCard,
  MetricRow,
  Pager,
  Select,
  SelectableChip,
  Stars,
  StatusPill,
  type DataTableColumn,
  PanelTitle,
} from '../../components';
import { formatCount } from '../../lib/format/money';
import { formatDate, type DayRange } from '../../lib/format/dates';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { LocalePicker } from '../locales/LocalePicker';
import { DetailDialogs, useDetailLinks } from '../orders/DetailDialogs';
import { listParam, numberParam, pickParam, stringParam } from '../profiles/people';
import { rangeTitle, useAnalyticsWindow } from '../analytics/parts';
import { TRIAGE_STATUSES, type FeedbackParams, type FeedbackRow, type FeedbackSortKey } from './api';
import { FeedbackDialog, TRIAGE_LABEL, TRIAGE_TONE } from './FeedbackDialog';
import { useFeedbackCount, useFeedbackList } from './queries';

const RATINGS = ['1', '2', '3', '4', '5'];

function columns(lowAt: number, openOrder: (id: string) => void): DataTableColumn<FeedbackRow>[] {
  return [
    { id: 'rating', header: 'Rating', width: '96px', sortKey: 'rating', cell: (r) => <Stars n={r.rating} /> },
    {
      id: 'comment',
      header: 'Comment',
      width: '2.4fr',
      wrap: true,
      cell: (r) => {
        const low = lowAt > 0 && r.rating <= lowAt;
        return (
          <span
            className={
              low
                ? 'line-clamp-3 text-[13px] font-semibold leading-[1.45] text-ink'
                : 'line-clamp-3 text-[13px] leading-[1.45] text-[rgba(42,22,9,.72)]'
            }
          >
            {r.note || <span className="font-normal text-muted">No comment</span>}
          </span>
        );
      },
    },
    {
      id: 'chef',
      header: 'Chef',
      width: '1.3fr',
      sortKey: 'chef',
      cell: (r) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <Avatar name={r.chefDisplayName} size="sm" />
          <span className="truncate text-[rgba(42,22,9,.78)]">{r.chefDisplayName}</span>
        </span>
      ),
    },
    {
      id: 'eater',
      header: 'Eater',
      width: '1.1fr',
      sortKey: 'eater',
      cell: (r) => <span className="truncate text-[rgba(42,22,9,.72)]">{r.eaterDisplayName}</span>,
    },
    {
      id: 'dish',
      header: 'Dish',
      width: '1.2fr',
      cell: (r) => <span className="truncate text-[rgba(42,22,9,.72)]">{r.dishTitle}</span>,
    },
    {
      id: 'order',
      header: 'Order',
      width: '112px',
      cell: (r) =>
        r.orderNumber ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation(); // the row opens the feedback; this opens the order
              openOrder(r.orderId);
            }}
            className="border-0 bg-transparent p-0 font-mono text-[12.5px] text-muted underline decoration-dotted underline-offset-2 hover:text-orange"
          >
            {r.orderNumber}
          </button>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      id: 'date',
      header: 'Date',
      width: '104px',
      sortKey: 'submittedAt',
      cell: (r) => <span className="text-muted">{formatDate(r.submittedAt)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '150px',
      sortKey: 'triageStatus',
      cell: (r) => (
        <StatusPill tone={TRIAGE_TONE[r.triageStatus]} dot>
          {TRIAGE_LABEL[r.triageStatus]}
        </StatusPill>
      ),
    },
  ];
}

/**
 * Feedback Triage (kit `FeedbackScreen`; FE plan §7). The eater-feedback queue
 * with its triage state. Filters, sort, page and the open submission live in
 * the URL. Default order (no sort) is the BE's: worst-rated first, then newest.
 */
export function FeedbackScreen() {
  const table = useTableUrlState();
  const { openOrder } = useDetailLinks();
  const f = table.filters;

  const status = pickParam(f.status, TRIAGE_STATUSES);
  const rating = numberParam(f.rating, 1, 5);
  const localeIds = listParam(f.localeIds);
  // One window for the page — the tiles and the list — All by default.
  const w = useAnalyticsWindow({ range: 'all' });
  const days = { start: w.window.startDate, end: w.window.endDate };
  const feedbackId = stringParam(f.feedback);

  const params: FeedbackParams = {
    page: table.page,
    pageSize: table.pageSize,
    triageStatus: status,
    ratingMin: rating,
    ratingMax: rating,
    localeIds,
    startDate: days.start,
    endDate: days.end,
    sortBy: table.sort?.key as FeedbackSortKey | undefined,
    order: table.sort?.dir,
  };
  const list = useFeedbackList(params, { silent: true });
  const lowAt = list.data?.lowRatingThreshold ?? 0;

  return (
    <div className="flex flex-col gap-[18px]">
      <div>
        <PanelTitle eyebrow="Triage snapshot" actions={<RangePicker value={w.range} onChange={w.setRange} />}>
          Feedback at a glance
        </PanelTitle>
        <FeedbackKpis localeIds={localeIds} lowAt={lowAt} days={days} rangeLabel={rangeTitle(w.range).toLowerCase()} />
      </div>

      <div>
        <FilterBar>
          <SelectableChip active={!status} onClick={() => table.setFilter('status', undefined)}>
            All
          </SelectableChip>
          {TRIAGE_STATUSES.map((s) => (
            <SelectableChip key={s} active={status === s} onClick={() => table.setFilter('status', s)}>
              {TRIAGE_LABEL[s]}
            </SelectableChip>
          ))}
          <FilterDivider />
          <Select
            aria-label="Rating"
            surface="white"
            compact
            style={{ width: 150 }}
            placeholder="Any rating"
            value={rating !== undefined ? String(rating) : ''}
            options={RATINGS.map((r) => ({ value: r, label: r === '1' ? '1 star' : `${r} stars` }))}
            onChange={(e) => table.setFilter('rating', e.target.value || undefined)}
          />
          <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
        </FilterBar>

        <DataTable
          label="Feedback"
          minWidth={1080}
          columns={columns(lowAt, openOrder)}
          rows={list.data?.items ?? []}
          getRowId={(r) => r.id}
          onRowClick={(r) => table.setParams({ feedback: r.id })}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={table.sort}
          onSortChange={table.setSort}
          emptyLabel="No feedback matches those filters."
        />
        <Pager
          page={table.page}
          pageSize={table.pageSize}
          total={list.data?.total ?? 0}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          noun="submissions"
        />
      </div>

      {feedbackId && (
        <FeedbackDialog
          id={feedbackId}
          onOpenFeedback={(id) => table.setParams({ feedback: id })}
          onClose={() => table.setParams({ feedback: undefined })}
        />
      )}
      <DetailDialogs />
    </div>
  );
}

/**
 * KPI tiles via pageSize=1 count reads (until BE ask B10); they follow the locale
 * filter. Submissions and low ratings are over the page's range; New and Needs
 * follow-up are what is waiting now, whenever it came in.
 */
function FeedbackKpis({
  localeIds,
  lowAt,
  days,
  rangeLabel,
}: {
  localeIds: string[];
  lowAt: number;
  days: DayRange;
  rangeLabel: string;
}) {
  const inRange = { localeIds, startDate: days.start, endDate: days.end };
  const submissions = useFeedbackCount(inRange);
  const fresh = useFeedbackCount({ localeIds, triageStatus: 'new' });
  const followUp = useFeedbackCount({ localeIds, triageStatus: 'follow_up' });
  const low = useFeedbackCount({ ...inRange, ratingMax: lowAt }, { enabled: lowAt > 0 });
  const n = (q: { data?: number; isError: boolean }) =>
    q.data !== undefined ? formatCount(q.data) : q.isError ? '—' : '…';

  return (
    <MetricRow>
      <MetricCard
        label="Submissions"
        value={n(submissions)}
        hint={rangeLabel}
        accent="var(--admin-chart-2)"
        icon={<MessageSquare size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="New"
        value={n(fresh)}
        hint="not yet triaged"
        accent="var(--admin-chart-1)"
        icon={<Inbox size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Needs follow-up"
        value={n(followUp)}
        accent="var(--admin-chart-4)"
        icon={<Flag size={15} aria-hidden="true" />}
      />
      <MetricCard
        label={lowAt > 0 ? `Low ratings (≤${lowAt}★)` : 'Low ratings'}
        value={lowAt > 0 ? n(low) : '…'}
        hint={rangeLabel}
        accent="var(--admin-chart-5)"
        icon={<TriangleAlert size={15} aria-hidden="true" />}
      />
    </MetricRow>
  );
}
